/* <vc-offers>: launcher tab + offers drawer. Escape and the overlay close it, focus is trapped while open and
   returned to the tab afterwards. "Hide" remembers the choice for the visit in sessionStorage. */
if (!customElements.get('vc-offers')) {
  const STORE_KEY = 'vc-offers-hidden';
  const store = {
    get() { try { return window.sessionStorage.getItem(STORE_KEY) === '1'; } catch (e) { return false; } },
    set() { try { window.sessionStorage.setItem(STORE_KEY, '1'); } catch (e) { /* storage blocked: hide for this page only */ } },
  };

  customElements.define('vc-offers', class extends HTMLElement {
    connectedCallback() {
      this.launch = this.querySelector('[data-launch]');
      this.tab = this.querySelector('[data-open]');
      this.drawer = this.querySelector('.vc-offers__drawer');
      this.panel = this.querySelector('.vc-offers__panel');
      this.status = this.querySelector('[data-status]');
      if (!this.launch || !this.drawer || !this.panel) return;
      if (store.get() && !window.Shopify?.designMode) this.launch.hidden = true;

      this.onKey = this.onKey.bind(this);
      this.tab.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-dismiss]').forEach((el) => el.addEventListener('click', () => this.dismiss()));
      this.querySelectorAll('[data-copy]').forEach((btn) => btn.addEventListener('click', () => this.copy(btn)));

      // Phones: keep the round button off the first screen and out of the way while scrolling down,
      // so it never sits over what is being read. It comes back on scroll up or once scrolling stops.
      const firstScreen = () => window.innerHeight * 0.75;
      this.lastY = window.scrollY;
      this.classList.toggle('is-tucked', this.lastY < firstScreen());
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          const y = window.scrollY;
          const dy = y - this.lastY;
          clearTimeout(this.restTimer);
          this.restTimer = setTimeout(() => this.classList.toggle('is-tucked', window.scrollY < firstScreen()), 700);
          if (Math.abs(dy) < 8) return;
          this.classList.toggle('is-tucked', y < firstScreen() || dy > 0);
          this.lastY = y;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // Step aside while the footer is on screen, so the launcher never covers the newsletter field or footer links.
      const footer = document.querySelector('footer.vc-footer, .vc-footer');
      if (footer && 'IntersectionObserver' in window) {
        this.footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.classList.toggle('is-at-footer', entry.isIntersecting));
        });
        this.footerObserver.observe(footer);
      }

      if (window.Shopify?.designMode) {
        const id = this.dataset.sectionId;
        this.onSelect = (e) => { if (e.detail.sectionId === id) { this.launch.hidden = false; this.open(false); } };
        this.onDeselect = (e) => { if (e.detail.sectionId === id) this.close(false); };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:block:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.restTimer);
      if (this.footerObserver) this.footerObserver.disconnect();
      document.body.classList.remove('vc-offers-lock');
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:block:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    open(moveFocus = true) {
      if (this.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      this.tab.setAttribute('aria-expanded', 'true');
      document.body.classList.add('vc-offers-lock');
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.classList.add('is-open');
        if (moveFocus) this.panel.focus({ preventScroll: true });
      }));
    }

    close(restoreFocus = true) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.tab.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('vc-offers-lock');
      document.removeEventListener('keydown', this.onKey);
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { this.drawer.hidden = true; }, 320);
      if (!restoreFocus) return;
      this.restoreFocus(!this.launch.hidden ? this.tab : this.lastFocus);
    }

    /* Focus goes back to the tab, or once the tab is hidden to where the shopper was. If that was the tab itself
       (or nothing), it goes to the main content, so keyboard and screen reader users are not left on a hidden button. */
    restoreFocus(target) {
      const usable = (el) => el && el !== document.body && typeof el.focus === 'function' && document.contains(el) && !(this.launch.hidden && this.launch.contains(el));
      const el = usable(target) ? target : document.getElementById('MainContent');
      if (el) el.focus({ preventScroll: true });
    }

    dismiss() {
      store.set();
      this.launch.hidden = true;
      if (this.classList.contains('is-open')) this.close();
      else this.restoreFocus(this.lastFocus);
    }

    onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!this.panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }

    async copy(btn) {
      const code = btn.dataset.copy || '';
      let ok = false;
      try {
        if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(code); ok = true; }
      } catch (e) { ok = false; }
      if (!ok) {
        const ta = document.createElement('textarea');
        ta.value = code; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        this.panel.appendChild(ta); ta.select();
        try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
        ta.remove();
        btn.focus({ preventScroll: true });
      }
      const label = btn.querySelector('[data-copy-label]');
      if (ok) {
        btn.classList.add('is-copied');
        if (label) label.textContent = 'Copied';
        if (this.status) this.status.textContent = `Code ${code} copied. Paste it at checkout.`;
      } else if (this.status) {
        this.status.textContent = `Copy did not work here. The code is ${code}.`;
      }
      clearTimeout(btn._vcTimer);
      btn._vcTimer = setTimeout(() => {
        btn.classList.remove('is-copied');
        if (label) label.textContent = 'Copy code';
      }, 2400);
    }
  });
}
